import {memo, ReactElement, useEffect, useRef, useState} from 'react';
import {renderMarkdownSummary} from 'markdown';
import {Repository} from 'types';
import {getRepositorySummary} from 'utils';

type ProjectDescriptionProps = {
  description?: string | null;
  fallback: string;
  repo?: Repository;
};

function ProjectDescription({description, fallback, repo}: ProjectDescriptionProps): ReactElement {
  const descriptionRef = useRef<HTMLParagraphElement | HTMLDivElement | null>(null);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [shouldRenderMarkdown, setShouldRenderMarkdown] = useState(false);
  const markdownText = description ? undefined : repo?.readme?.text;

  useEffect(() => {
    setMarkdown(null);
    setShouldRenderMarkdown(false);
  }, [markdownText]);

  useEffect(() => {
    if (!markdownText) {
      return undefined;
    }

    if (!('IntersectionObserver' in window)) {
      setShouldRenderMarkdown(true);

      return undefined;
    }

    const element = descriptionRef.current;

    if (!element) {
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShouldRenderMarkdown(true);
        observer.disconnect();
      }
    }, {rootMargin: '260px'});

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [markdownText]);

  useEffect(() => {
    if (!markdownText || !shouldRenderMarkdown) {
      return undefined;
    }

    let active = true;

    renderMarkdownSummary(markdownText)
      .then((html) => {
        if (active) {
          setMarkdown(html);
        }
      })
      .catch(() => {
        if (active) {
          setMarkdown(null);
        }
      });

    return () => {
      active = false;
    };
  }, [markdownText, shouldRenderMarkdown]);

  if (description) {
    return <p className="description">{description}</p>;
  }

  if (markdown) {
    return (
      <div
        ref={descriptionRef}
        className="description markdown-description"
        dangerouslySetInnerHTML={{__html: markdown}}
      />
    );
  }

  return <p className="description" ref={descriptionRef}>{repo ? getRepositorySummary(repo) : fallback}</p>;
}

export default memo(ProjectDescription);
